import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Utensils } from 'lucide-react';
import api from '../../config/api';
import userAPI from '../../services/userAPI';
import { getCatalog } from '../../services/catalogAPI';
import { getAbsoluteImageUrl } from '../../utils/imageUrl';
import FormBanner from '../Common/FormBanner';
import './Profile.css';

// Same preferences as the app's Food Preferences screen: the admin cuisine list,
// stored as cuisine names on the user.
const FoodPreferences = () => {
  const navigate = useNavigate();
  const [cuisines, setCuisines] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({ type: '', text: '' });

  useEffect(() => {
    Promise.all([getCatalog(), userAPI.getProfile()])
      .then(([catalog, profile]) => {
        setCuisines((catalog.cuisines || []).filter((c) => c?.name));
        setSelected(profile?.foodPreferences || profile?.user?.foodPreferences || []);
      })
      .catch(() => setBanner({ type: 'error', text: 'Could not load cuisines. Please try again.' }))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (name) =>
    setSelected((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const save = async () => {
    setSaving(true);
    setBanner({ type: '', text: '' });
    try {
      await api.post('/user/food-preferences', { foodPreferences: selected });
      setBanner({ type: 'success', text: 'Your food preferences have been saved.' });
    } catch (err) {
      setBanner({ type: 'error', text: err?.response?.data?.message || 'Could not save your preferences.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-dashboard-page">
      <div className="premium-profile-header-container" style={{ paddingBottom: '60px' }}>
        <button onClick={() => navigate(-1)} className="header-back-btn" aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="header-profile-info">
          <div className="icon-circle" style={{ background: 'white', color: '#C5192D', width: '60px', height: '60px', marginBottom: '12px' }}>
            <Utensils size={30} />
          </div>
          <h2 className="dashboard-user-name">Food Preferences</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0 0', fontSize: '14px' }}>
            Pick the cuisines you love to personalise your recommendations
          </p>
        </div>
      </div>

      <div className="dashboard-content" style={{ marginTop: '-30px' }}>
        <FormBanner message={banner.text} type={banner.type || 'error'} onDismiss={() => setBanner({ type: '', text: '' })} />
        {loading ? (
          <p style={{ textAlign: 'center', color: '#64748b', padding: '40px 0' }}>Loading…</p>
        ) : cuisines.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#64748b', padding: '40px 0' }}>No cuisines available yet.</p>
        ) : (
          <div className="food-pref-grid">
            {cuisines.map((cuisine) => {
              const active = selected.includes(cuisine.name);
              return (
                <button
                  key={cuisine._id || cuisine.name}
                  type="button"
                  className={`food-pref-card${active ? ' active' : ''}`}
                  onClick={() => toggle(cuisine.name)}
                  aria-pressed={active}
                  style={cuisine.image ? { backgroundImage: `url(${getAbsoluteImageUrl(cuisine.image)})` } : undefined}
                >
                  <span className="food-pref-name">{cuisine.name}</span>
                  {active && <span className="food-pref-check"><Check size={14} /></span>}
                </button>
              );
            })}
          </div>
        )}
        <button type="button" className="food-pref-save" onClick={save} disabled={saving || loading}>
          {saving ? 'Saving…' : `Save preferences${selected.length ? ` (${selected.length})` : ''}`}
        </button>
      </div>
    </div>
  );
};

export default FoodPreferences;
